import Phaser from "phaser";

import type { MountContext } from "../../registry";
import type { ProgressEntry } from "./PlayScene";

interface UISceneData {
  ctx: MountContext;
}

interface HudPayload {
  lives?: number;
  maxLives?: number;
  xp?: number;
  gameOver?: boolean;
  rescued?: boolean;
  training?: boolean;
}

const HEART_FULL = "❤";
const HEART_EMPTY = "♡";
const PORTRAIT_CONTROLS_HEIGHT = 130;
const LANDSCAPE_CONTROLS_HEIGHT = 110;

/** Siehe `PlayScene.controlsReservedHeight` – muss mit dem dortigen Wert übereinstimmen. */
function controlsReservedHeight(w: number, h: number): number {
  if (navigator.maxTouchPoints === 0) return 0;
  if (w < h && w < 900) return PORTRAIT_CONTROLS_HEIGHT;
  if (w >= h && h < 600) return LANDSCAPE_CONTROLS_HEIGHT;
  return 0;
}

/** Visualisierung eines einzelnen Spielers auf dem Progress-Balken. */
interface ProgressMarker {
  bg: Phaser.GameObjects.Arc;
  dot: Phaser.GameObjects.Arc;
  label: Phaser.GameObjects.Text;
}

const PROGRESS_TRACK_HEIGHT = 6;

export class UIScene extends Phaser.Scene {
  private ctx!: MountContext;
  private hint!: Phaser.GameObjects.Text;
  private heartText!: Phaser.GameObjects.Text;
  private xpText!: Phaser.GameObjects.Text;
  private roomText!: Phaser.GameObjects.Text;
  private modeBadge!: Phaser.GameObjects.Text;
  private currentLives = 3;
  private currentMaxLives = 3;
  private currentXp = 0;
  private training = false;
  private gameOverShown = false;

  private progressTrack!: Phaser.GameObjects.Rectangle;
  private progressFill!: Phaser.GameObjects.Rectangle;
  private progressGoal!: Phaser.GameObjects.Text;
  private progressMarkers = new Map<string, ProgressMarker>();
  private progressLastEntries: ProgressEntry[] = [];

  constructor() {
    super("UIScene");
  }

  init(data: UISceneData) {
    this.ctx = data.ctx;
  }

  create() {
    this.training = this.ctx.room.gameMode === "training";
    const topPad = 12;
    this.heartText = this.add
      .text(12, topPad, this.renderHearts(this.currentLives, this.currentMaxLives), {
        fontFamily: "Inter, sans-serif",
        fontSize: "20px",
        color: "#ef4444",
        backgroundColor: "rgba(0,0,0,0.5)",
        padding: { x: 10, y: 4 },
      })
      .setScrollFactor(0)
      .setDepth(50);

    this.modeBadge = this.add
      .text(12, topPad + 70, "TRAINING", {
        fontFamily: "Inter, sans-serif",
        fontSize: "11px",
        fontStyle: "bold",
        color: "#0b0d17",
        backgroundColor: "#38bdf8",
        padding: { x: 8, y: 3 },
      })
      .setScrollFactor(0)
      .setDepth(50)
      .setVisible(this.training);

    this.xpText = this.add
      .text(this.scale.width / 2, topPad, "XP 0", {
        fontFamily: "Inter, sans-serif",
        fontSize: "18px",
        color: "#fde047",
        backgroundColor: "rgba(0,0,0,0.5)",
        padding: { x: 10, y: 4 },
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(50);

    this.roomText = this.add
      .text(this.scale.width - 12, topPad, `Raum ${this.ctx.room.code}`, {
        fontFamily: "Inter, sans-serif",
        fontSize: "12px",
        color: "#c4b5fd",
        backgroundColor: "rgba(0,0,0,0.45)",
        padding: { x: 8, y: 4 },
      })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(50);

    this.hint = this.add
      .text(
        12,
        topPad + 36,
        "← → laufen  ·  ⎵ / W = Sprung  ·  F / J / K = Schuss",
        {
          fontFamily: "Inter, sans-serif",
          fontSize: "13px",
          fontStyle: "bold",
          color: "#fde047",
          backgroundColor: "rgba(0,0,0,0.6)",
          padding: { x: 10, y: 5 },
        },
      )
      .setScrollFactor(0)
      .setDepth(49);

    // Progress-Balken (Strecke bis Anna). Mittig oben, unter dem XP-Badge.
    this.progressTrack = this.add
      .rectangle(this.scale.width / 2, topPad + 38, 200, PROGRESS_TRACK_HEIGHT, 0xffffff, 0.18)
      .setOrigin(0.5, 0.5)
      .setScrollFactor(0)
      .setDepth(48);
    this.progressFill = this.add
      .rectangle(this.scale.width / 2, topPad + 38, 0, PROGRESS_TRACK_HEIGHT, 0xfbbf24, 0.55)
      .setOrigin(0, 0.5)
      .setScrollFactor(0)
      .setDepth(49);
    this.progressGoal = this.add
      .text(0, 0, "🏰", {
        fontFamily: "Inter, sans-serif",
        fontSize: "14px",
      })
      .setOrigin(0.5, 0.5)
      .setScrollFactor(0)
      .setDepth(50);

    this.applyViewport();
    this.scale.on("resize", this.layout, this);
    this.events.on("hud", this.applyHud, this);
    this.events.on("progress", this.applyProgress, this);

    this.time.delayedCall(11000, () => {
      this.tweens.add({ targets: this.hint, alpha: 0, duration: 1200 });
    });

    this.events.once("shutdown", () => {
      this.scale.off("resize", this.layout, this);
      this.events.off("hud", this.applyHud, this);
      this.events.off("progress", this.applyProgress, this);
    });
  }

  private applyViewport() {
    const w = this.scale.width;
    const h = this.scale.height;
    const viewportH = h - controlsReservedHeight(w, h);
    this.cameras.main.setViewport(0, 0, w, viewportH);
  }

  private layout = () => {
    this.applyViewport();
    const topPad = 12;
    const cam = this.cameras.main;
    const vpW = cam.width;
    this.heartText.setPosition(12, topPad);
    this.xpText.setPosition(vpW / 2, topPad);
    this.roomText.setPosition(vpW - 12, topPad);
    this.hint.setPosition(12, topPad + 36);
    this.modeBadge.setPosition(12, topPad + 70);

    // Progress-Balken zentrieren – Breite skaliert mit Viewport, ca. 60 %
    // davon, max 360 px (für Tablet/Desktop)
    const trackW = Math.min(360, Math.max(180, vpW * 0.55));
    const trackX = vpW / 2;
    const trackY = topPad + 38;
    this.progressTrack
      .setPosition(trackX, trackY)
      .setSize(trackW, PROGRESS_TRACK_HEIGHT);
    this.progressFill
      .setPosition(trackX - trackW / 2, trackY)
      .setSize(0, PROGRESS_TRACK_HEIGHT);
    this.progressGoal.setPosition(trackX + trackW / 2 + 12, trackY);
    // Marker-Positionen neu zeichnen, falls schon Daten da sind
    this.renderProgressMarkers();
  };

  private applyHud = (payload: HudPayload) => {
    if (typeof payload.training === "boolean") this.training = payload.training;
    if (typeof payload.lives === "number") this.currentLives = payload.lives;
    if (typeof payload.maxLives === "number") this.currentMaxLives = payload.maxLives;
    if (typeof payload.xp === "number") this.currentXp = payload.xp;
    this.heartText.setText(this.renderHearts(this.currentLives, this.currentMaxLives));
    this.xpText
      .setText(this.training ? "XP — (Training)" : `XP ${this.currentXp}`)
      .setColor(this.training ? "#94a3b8" : "#fde047");
    this.modeBadge.setVisible(this.training);

    // Im Training niemals Game-Over zeigen.
    if (payload.gameOver && !this.training && !this.gameOverShown) {
      this.gameOverShown = true;
      this.showGameOver(payload.rescued === true);
    }
  };

  private applyProgress = (entries: ProgressEntry[]) => {
    this.progressLastEntries = entries;
    this.renderProgressMarkers();
  };

  private renderProgressMarkers() {
    const entries = this.progressLastEntries;
    const trackW = this.progressTrack.width;
    const trackX = this.progressTrack.x;
    const trackY = this.progressTrack.y;
    const startX = trackX - trackW / 2;

    // Eigenen Fortschritt als gefüllter Balken
    const myEntry = entries.find((e) => e.isMe);
    if (myEntry) {
      this.progressFill.setSize(trackW * myEntry.progress, PROGRESS_TRACK_HEIGHT);
    }

    const seen = new Set<string>();
    for (const entry of entries) {
      seen.add(entry.id);
      const x = startX + trackW * entry.progress;
      const colorNum = Phaser.Display.Color.HexStringToColor(entry.color).color;
      let marker = this.progressMarkers.get(entry.id);
      if (!marker) {
        const bg = this.add
          .circle(x, trackY, 7, 0x0b0d17, 1)
          .setStrokeStyle(1.5, 0xffffff, 0.8)
          .setScrollFactor(0)
          .setDepth(50);
        const dot = this.add
          .circle(x, trackY, 4, colorNum, 1)
          .setScrollFactor(0)
          .setDepth(51);
        const label = this.add
          .text(x, trackY + 10, "", {
            fontFamily: "Inter, sans-serif",
            fontSize: "10px",
            fontStyle: entry.isMe ? "bold" : "normal",
            color: entry.isMe ? "#fde047" : "#e7e9f4",
            backgroundColor: "rgba(0,0,0,0.55)",
            padding: { x: 4, y: 1 },
          })
          .setOrigin(0.5, 0)
          .setScrollFactor(0)
          .setDepth(52);
        marker = { bg, dot, label };
        this.progressMarkers.set(entry.id, marker);
      }
      marker.bg.setPosition(x, trackY);
      marker.dot.setPosition(x, trackY).setFillStyle(colorNum, 1);
      marker.dot.setAlpha(entry.alive ? 1 : 0.4);
      marker.label
        .setPosition(x, trackY + 10)
        .setText(`${entry.isMe ? "Du" : entry.name} · ${Math.round(entry.progress * 100)}%`);
      // Nur eigene Label dauerhaft zeigen, sonst flackert es bei vielen Spielern.
      marker.label.setVisible(entry.isMe);
    }

    // Verschwundene Spieler entfernen
    for (const [id, marker] of this.progressMarkers) {
      if (!seen.has(id)) {
        marker.bg.destroy();
        marker.dot.destroy();
        marker.label.destroy();
        this.progressMarkers.delete(id);
      }
    }
  }

  private renderHearts(lives: number, max: number): string {
    // Im Training symbolisch unendlich.
    if (this.training) return "∞";
    const safeMax = Math.max(lives, max, 0);
    return Array.from({ length: safeMax })
      .map((_, i) => (i < lives ? HEART_FULL : HEART_EMPTY))
      .join(" ");
  }

  private showGameOver(rescued: boolean) {
    const cam = this.cameras.main;
    const w = cam.width;
    const h = cam.height;

    this.add
      .rectangle(0, 0, w, h, 0x000000, 0.65)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(900);

    const title = rescued ? "Mission erfüllt!" : "Game Over";
    const sub = rescued
      ? `Team hat die Princess gerettet  ·  XP ${this.currentXp}`
      : `Keine Leben mehr  ·  XP ${this.currentXp}`;

    this.add
      .text(w / 2, h / 2 - 30, title, {
        fontFamily: "Inter, sans-serif",
        fontSize: "44px",
        fontStyle: "bold",
        color: "#fff",
        stroke: rescued ? "#ec4899" : "#dc2626",
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(901);

    this.add
      .text(w / 2, h / 2 + 24, sub, {
        fontFamily: "Inter, sans-serif",
        fontSize: "18px",
        color: "#e7e9f4",
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(901);

    this.add
      .text(w / 2, h / 2 + 70, "Verlasse das Spiel über die Toolbar oben, um neu zu starten.", {
        fontFamily: "Inter, sans-serif",
        fontSize: "13px",
        color: "#c4b5fd",
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(901);

    void cam;
  }
}
